import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { logger } from '@/utils/productionLogger';

interface UseIdleTimerOptions {
  timeout?: number; // Timeout en millisecondes (défaut: 15 minutes)
  events?: string[]; // Événements à écouter pour détecter l'activité
  enabled?: boolean; // Activer/désactiver le timer
}

/**
 * Hook pour gérer la déconnexion automatique après inactivité
 */
export const useIdleTimer = ({
  timeout = 15 * 60 * 1000, // 15 minutes par défaut
  events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
  enabled = true
}: UseIdleTimerOptions = {}) => {
  const { user, signOut } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const lastActivityRef = useRef<number>(Date.now());

  // Fonction de déconnexion
  const handleTimeout = useCallback(async () => {
    if (!user) return;
    
    logger.info('Déconnexion automatique après inactivité', {
      userId: user.id,
      lastActivity: new Date(lastActivityRef.current).toISOString(),
      timeoutMinutes: timeout / (60 * 1000)
    });
    
    await signOut();
  }, [user, signOut, timeout]);

  // Réinitialiser le timer
  const resetTimer = useCallback(() => {
    if (!enabled || !user) return;

    lastActivityRef.current = Date.now();
    
    // Nettoyer le timer existant
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Créer un nouveau timer
    timeoutRef.current = setTimeout(handleTimeout, timeout);
  }, [enabled, user, handleTimeout, timeout]);

  // Gestionnaire d'événements d'activité
  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled || !user) {
      // Nettoyer le timer si désactivé ou pas d'utilisateur
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      return;
    }

    // Démarrer le timer
    resetTimer();

    // Ajouter les listeners d'événements
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Nettoyage
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [enabled, user, events, handleActivity, resetTimer]);

  // Nettoyer à la déconnexion du composant
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    resetTimer,
    lastActivity: lastActivityRef.current
  };
};